<template>
  <form v-on:submit.prevent="toggleEdit">
    <div class="field is-grouped mt-1 mb-0">
      <div class="control mr-1">
        <input
          class="input"
          type="text"
          v-model="setName"
          :class="{ 'is-danger': updateError.name, 'is-static pl-2 pr-3': !editing }"
          :disabled="loading || !canEdit || !editing || undefined"
        />
      </div>
      <div class="control mr-1">
        <button
          type="submit"
          class="button"
          :class="{ 'is-loading': loading, 'has-text-primary': !editing, 'is-primary': editing }"
          :disabled="loading || !canEdit || undefined"
          :title="updateTitle"
        >
          <i class="icon-edit"></i>
        </button>
      </div>
      <div class="control">
        <button
          type="button"
          class="button has-text-danger"
          :class="{ 'is-loading': loading }"
          :disabled="loading || !canDelete || undefined"
          v-on:click="setDelete"
          :title="deleteTitle"
        >
          <i class="icon-trash"></i>
        </button>
      </div>
    </div>
    <template v-if="updateError.name">
      <p
        v-for="(err, i) in updateError.name"
        :key="i"
        class="help has-text-danger"
      >
        {{ err }}
      </p>
    </template>
  </form>
</template>

<script lang="ts">
import { isAxiosError } from 'axios'
import { mapActions, mapState } from 'pinia'
import { type PropType, defineComponent } from 'vue'

import { corporaMixin } from '@/mixins'
import { useAuthStore, useDatasetStore, useNotificationStore } from '@/stores'
import type { UUID } from '@/types'
import type { Dataset, DatasetSet } from '@/types/dataset'

export default defineComponent({
  mixins: [corporaMixin],
  props: {
    datasetSet: {
      type: Object as PropType<DatasetSet>,
      required: true,
    },
    dataset: {
      type: Object as PropType<Dataset>,
      required: true,
    },
    // Used to reset the set name field when the modal is closed
    modalOpen: {
      type: Boolean,
      required: true,
    },
  },
  data: () => ({
    editing: false,
    loading: false,
    setName: '',
    updateError: { name: '' },
  }),
  mounted() {
    this.setName = this.datasetSet.name
  },
  computed: {
    ...mapState(useAuthStore, ['isVerified']),
    hasContribPrivilege(): boolean {
      return this.isVerified && this.canWrite(this.corpus)
    },
    hasAdminPrivilege(): boolean {
      return this.isVerified && this.canAdmin(this.corpus)
    },
    canEdit(): boolean {
      return this.hasContribPrivilege && this.dataset.state === 'open'
    },
    canDelete(): boolean {
      return this.hasAdminPrivilege && this.dataset.state === 'open' && this.dataset.sets.length > 1
    },
    corpusId(): UUID {
      return this.dataset.corpus_id
    },
    updateTitle(): string | undefined {
      if (this.dataset.state !== 'open') return 'Sets can only be edited on open datasets'
      else if (!this.hasContribPrivilege)
        return "You must be a contributor on the project to edit a dataset's sets"
      else if (this.editing) return 'Save set name'
      else if (this.canEdit) return 'Edit set name'
      return undefined
    },
    deleteTitle(): string {
      if (this.dataset.state !== 'open') return 'Sets can only be deleted on open datasets'
      else if (!this.hasAdminPrivilege)
        return 'You must be an administrator on the project to delete dataset sets'
      else if (this.dataset.sets.length <= 1)
        return 'This is the only set in the dataset and cannot be deleted'
      return 'Delete set'
    },
  },
  methods: {
    ...mapActions(useDatasetStore, ['updateDatasetSet', 'deleteDatasetSet']),
    ...mapActions(useNotificationStore, ['notify']),
    async toggleEdit() {
      if (!this.canEdit) return
      // When starting to edit, just enable the form
      if (!this.editing) {
        this.editing = true
        return
      }
      // When completing an edit, save the form
      this.loading = true
      try {
        await this.updateDatasetSet(this.dataset.id, this.datasetSet.id, this.setName)
        this.notify({ type: 'success', text: 'Dataset set updated' })
        this.updateError.name = ''
        this.editing = false
      } catch (err) {
        if (
          isAxiosError(err) &&
          err.response?.status === 400 &&
          err.response.data &&
          'name' in err.response.data
        ) {
          this.updateError.name = err.response.data.name
        }
      } finally {
        this.loading = false
      }
    },
    async setDelete() {
      this.loading = true
      if (!this.canDelete) return
      // Errors are handled (notification) in the store
      try {
        await this.deleteDatasetSet(this.dataset.id, this.datasetSet.id)
        this.editing = false
      } finally {
        this.loading = false
      }
    },
  },
  watch: {
    modalOpen(newValue: boolean) {
      // Reset set name field and errors when the modal is closed
      if (newValue === false) {
        this.setName = this.datasetSet.name
        this.updateError.name = ''
        this.editing = false
      }
    },
  },
})
</script>
